/** これ以上 main thread が止まったら、クリックが処理されない程度の停止として Pendo に送る */
const LONG_TASK_REPORT_MS = 2000; // heuristic value
/** Pendo に送る long task は、この間隔に1件まで */
const LONG_TASK_REPORT_INTERVAL_MS = 60 * 1000; // heuristic value
/** nav_stall の集計に使うため、この時間分の long task を覚えておく */
const LONG_TASK_HISTORY_MS = 2 * 60 * 1000; // heuristic value

interface LongTask {
  startTime: number;
  duration: number;
}

interface ResourceTimingLike {
  name: string;
  initiatorType: string;
  responseEnd: number;
}

/** `tasks` に `task` を足し、{@link LONG_TASK_HISTORY_MS} より古いものを捨てる */
function recordLongTask(tasks: LongTask[], task: LongTask, now: number): void {
  tasks.push({ startTime: task.startTime, duration: task.duration });
  const firstKept = tasks.findIndex((t) => t.startTime + LONG_TASK_HISTORY_MS >= now);
  tasks.splice(0, firstKept === -1 ? tasks.length : firstKept);
}

/** `since` 以降に main thread が止まっていた合計の ms */
function sumLongTaskSince(tasks: readonly LongTask[], since: number): number {
  let sum = 0;
  for (const task of tasks) {
    const end = task.startTime + task.duration;
    if (end > since) {
      sum += end - Math.max(task.startTime, since);
    }
  }
  return Math.round(sum);
}

function shouldReportLongTask(duration: number, lastReportedAt: number | undefined, now: number): boolean {
  if (duration < LONG_TASK_REPORT_MS) {
    return false;
  }
  return lastReportedAt === undefined || now - lastReportedAt >= LONG_TASK_REPORT_INTERVAL_MS;
}

/** `time` より前に完了した API 呼び出しのうち、最後のものの pathname */
function findLastApiBefore(
  entries: readonly (PerformanceEntry | ResourceTimingLike)[],
  time: number,
): string | undefined {
  let last: ResourceTimingLike | undefined;
  for (const entry of entries) {
    if (!('initiatorType' in entry && 'responseEnd' in entry)) {
      continue;
    }
    const isApi = entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch';
    if (isApi && entry.responseEnd <= time && (!last || entry.responseEnd > last.responseEnd)) {
      last = entry;
    }
  }
  if (!last) {
    return undefined;
  }
  try {
    return new URL(last.name).pathname;
  } catch {
    return undefined;
  }
}

export type { LongTask, ResourceTimingLike };
export {
  findLastApiBefore,
  LONG_TASK_HISTORY_MS,
  LONG_TASK_REPORT_INTERVAL_MS,
  LONG_TASK_REPORT_MS,
  recordLongTask,
  shouldReportLongTask,
  sumLongTaskSince,
};
